import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Download } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api, formatErr, mediaUrl } from "@/lib/api";

export const isSubscribed = () => localStorage.getItem("bt_subscribed") === "1";

export default function EmailCapture({ variant = "growth", source = "website", compact }) {
  const { settings } = useSite();
  const isLead = variant === "lead";
  const ec = settings.email_capture;
  const lm = settings.lead_magnet;
  const [form, setForm] = useState({ first_name: "", email: "", consent: false });
  const [state, setState] = useState({ loading: false, error: "", done: false, file: "" });
  const p = `${isLead ? "lead" : "email"}-capture`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return setState((s) => ({ ...s, error: "Please tick the box so we can email you." }));
    setState({ loading: true, error: "", done: false });
    try {
      const { data } = await api.post("/subscribe", { ...form, source: isLead ? "lead_magnet" : source });
      localStorage.setItem("bt_subscribed", "1");
      setState({ loading: false, error: "", done: true, file: data.file_url || "" });
    } catch (err) {
      setState({ loading: false, error: formatErr(err), done: false });
    }
  };

  if (state.done)
    return (
      <div className="flex items-start gap-4 rounded-3xl bg-bt-sage p-6" data-testid={`${p}-success`}>
        <CheckCircle2 className="mt-0.5 shrink-0 text-bt-primary" />
        <div>
          <p className="font-heading text-xl text-bt-heading">{isLead ? lm.success : ec.success}</p>
          {isLead && lm.challenge_url && (
            <Link to={lm.challenge_url} className="btn-primary mr-3 mt-4" data-testid={`${p}-start-challenge`}>Start the challenge</Link>
          )}
          {state.file && (
            <a href={mediaUrl(state.file)} target="_blank" rel="noopener noreferrer" className="btn-primary mt-4" data-testid={`${p}-download`}>
              <Download size={18} /> Download now
            </a>
          )}
        </div>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${p}-form`}>
      <div className={`grid gap-3 ${compact ? "" : "sm:grid-cols-2"}`}>
        <input className="input-bt" placeholder="First name" required value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid={`${p}-first-name-input`} />
        <input className="input-bt" type="email" placeholder="Email address" required value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`${p}-email-input`} />
      </div>
      <label className="flex cursor-pointer items-start gap-3 py-1 text-sm text-bt-text">
        <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[color:var(--bt-primary)]" checked={form.consent}
          onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid={`${p}-consent-checkbox`} />
        <span>{ec.consent_text}</span>
      </label>
      {state.error && <p className="text-sm text-red-700" data-testid={`${p}-error`}>{state.error}</p>}
      <button className="btn-primary w-full sm:w-auto" disabled={state.loading} data-testid={`${p}-submit-button`}>
        {state.loading ? "Joining..." : isLead ? lm.button : ec.button}
      </button>
      <p className="text-xs text-bt-sagedeep">
        {ec.privacy_text} <a href="/p/privacy" className="underline">Privacy</a>
      </p>
    </form>
  );
}

export function GrowthListBlock({ source }) {
  const { settings } = useSite();
  const [hidden] = useState(isSubscribed);
  if (hidden) return null;
  const ec = settings.email_capture;
  return (
    <section className="relative overflow-hidden rounded-[32px] border border-bt-border bg-bt-surface p-7 sm:p-12" data-testid="growth-list-block">
      <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center">
        <div>
          <h2 className="text-3xl leading-tight sm:text-4xl">{ec.headline}</h2>
          <p className="mt-4 text-base text-bt-text md:text-lg">{ec.body}</p>
        </div>
        <EmailCapture source={source} />
      </div>
    </section>
  );
}

export function SignupBar({ s }) {
  const [hidden] = useState(isSubscribed);
  if (hidden) return null;
  return (
    <section className="border-b border-bt-border bg-bt-sage" data-testid="home-email-signup">
      <div className="container-bt grid gap-5 py-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:py-10">
        <div>
          <h2 className="text-2xl leading-tight sm:text-3xl">{s.headline}</h2>
          {s.subheadline && <p className="mt-2 text-base">{s.subheadline}</p>}
        </div>
        <EmailCapture source="home_top" />
      </div>
    </section>
  );
}
